import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

// Shared module-level store so every useUserBlocks() instance (profile page,
// popup, messages, etc.) sees block/unblock changes immediately, without a refresh.
let sharedBlockedIds: string[] = [];
let sharedLoadedFor: string | null = null;
const listeners = new Set<(ids: string[]) => void>();

const emit = () => {
  listeners.forEach((listener) => listener(sharedBlockedIds));
};

const fetchBlocksFor = async (userId: string) => {
  try {
    const { data, error } = await (supabase
      .from("user_blocks" as any)
      .select("blocked_user_id")
      .eq("blocker_id", userId) as any);

    if (error) throw error;
    sharedBlockedIds = (data as any[])?.map((b: any) => b.blocked_user_id) || [];
    sharedLoadedFor = userId;
    emit();
  } catch (err) {
    console.error("Error fetching blocks:", err);
  }
};

export const useUserBlocks = () => {
  const { user } = useAuth();
  const [blockedUserIds, setBlockedUserIds] = useState<string[]>(sharedBlockedIds);
  const [loading, setLoading] = useState(!user || sharedLoadedFor !== user.id);

  useEffect(() => {
    const listener = (ids: string[]) => setBlockedUserIds(ids);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      sharedBlockedIds = [];
      sharedLoadedFor = null;
      setBlockedUserIds([]);
      setLoading(false);
      return;
    }
    if (sharedLoadedFor !== user.id) {
      setLoading(true);
      fetchBlocksFor(user.id).finally(() => setLoading(false));
    } else {
      setBlockedUserIds(sharedBlockedIds);
      setLoading(false);
    }
  }, [user]);

  const blockUser = async (blockedUserId: string) => {
    if (!user) return false;
    try {
      const { error } = await (supabase
        .from("user_blocks" as any)
        .insert({ blocker_id: user.id, blocked_user_id: blockedUserId } as any) as any);

      if (error) {
        if (error.code === "23505") {
          toast.info("User is already blocked");
          if (!sharedBlockedIds.includes(blockedUserId)) {
            sharedBlockedIds = [...sharedBlockedIds, blockedUserId];
            emit();
          }
          return true;
        }
        throw error;
      }

      sharedBlockedIds = [...sharedBlockedIds, blockedUserId];
      emit();
      toast.success("User blocked");

      // Also remove any friend connection
      await supabase
        .from("friend_requests")
        .delete()
        .or(
          `and(sender_id.eq.${user.id},receiver_id.eq.${blockedUserId}),and(sender_id.eq.${blockedUserId},receiver_id.eq.${user.id})`
        );
      return true;
    } catch (err) {
      console.error("Error blocking user:", err);
      toast.error("Failed to block user");
      return false;
    }
  };

  const unblockUser = async (blockedUserId: string) => {
    if (!user) return false;
    try {
      const { error } = await (supabase
        .from("user_blocks" as any)
        .delete()
        .eq("blocker_id", user.id)
        .eq("blocked_user_id", blockedUserId) as any);

      if (error) throw error;
      sharedBlockedIds = sharedBlockedIds.filter((id) => id !== blockedUserId);
      emit();
      toast.success("User unblocked");
      return true;
    } catch (err) {
      console.error("Error unblocking user:", err);
      toast.error("Failed to unblock user");
      return false;
    }
  };

  const isBlocked = useCallback(
    (userId: string) => blockedUserIds.includes(userId),
    [blockedUserIds]
  );

  const refetch = useCallback(async () => {
    if (user) await fetchBlocksFor(user.id);
  }, [user]);

  return { blockedUserIds, loading, blockUser, unblockUser, isBlocked, refetch };
};
